/**
 * UranIA PA — Hook stato onboarding del Funzionario AdG.
 *
 * Determina se l'utente autenticato (con ruolo ``funzionario_adg``) ha
 * gia' completato l'onboarding PA. E' la pre-condizione UX per accedere
 * al workspace Mod5: lato server l'autorizzazione resta sulle rotte
 * documenti (US-002 + step-up), qui copriamo il flusso utente.
 *
 * Logica:
 *
 *   1. Se il principal non e' funzionario -> ``not_required`` (passa).
 *   2. Snapshot localStorage (``urania.onboarding.pa.session.v1``) gia'
 *      popolato dalla route ``/onboarding/pa``: se ``completed=true``
 *      sblocchiamo subito senza round-trip.
 *   3. Se snapshot assente o non completo, facciamo POST idempotente a
 *      ``/api/v1/onboarding/pa/sessions``: in caso di session pre-esistente
 *      il backend risponde 200 con ``completed_at`` valorizzato, in caso
 *      di nuovo profilo 201 con ``completed_at=null``. Salviamo lo
 *      snapshot per le successive route, evitando ulteriori POST.
 *
 * NB: il POST di apertura session **non** ha effetti collaterali se
 * eseguito sul funzionario gia' onboarded — il handler e' idempotente
 * sulla ``UNIQUE (tenant_id, user_id, profile_type='pa')``.
 */

import { useEffect, useRef, useState } from 'react';

import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../auth/auth-store.ts';
import { principalIsFunzionario } from '../auth/rbac.ts';
import { createOnboardingPaSession, OnboardingApiError } from './pa-api.ts';
import {
  loadOnboardingPaSnapshot,
  saveOnboardingPaSnapshot,
  snapshotFromSessionDto,
} from './pa-session-storage.ts';

export type FunzionarioOnboardingPhase =
  | { kind: 'not_required' }
  | { kind: 'loading' }
  | { kind: 'completed' }
  | { kind: 'incomplete' }
  | { kind: 'error'; message: string };

/**
 * Ritorna lo stato corrente dell'onboarding del Funzionario AdG.
 *
 * Il caller (route guard) e' atteso re-renderizzare quando lo stato
 * passa da ``loading`` a ``completed``/``incomplete``.
 */
export function useFunzionarioOnboardingStatus(): FunzionarioOnboardingPhase {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const subject = principal?.subject ?? '';
  const isFunzionario = principalIsFunzionario(principal);
  const requestedRef = useRef(false);

  const initialFromSnapshot = (): FunzionarioOnboardingPhase => {
    if (!isAuthenticated || !isFunzionario) {
      return { kind: 'not_required' };
    }
    if (subject.length === 0) {
      return { kind: 'loading' };
    }
    const snap = loadOnboardingPaSnapshot(subject);
    if (snap !== null && snap.completed) {
      return { kind: 'completed' };
    }
    return { kind: 'loading' };
  };

  const [phase, setPhase] = useState<FunzionarioOnboardingPhase>(initialFromSnapshot);

  useEffect(() => {
    // Re-allinea allo stato del principal se cambia mid-session.
    if (!isAuthenticated || !isFunzionario) {
      setPhase({ kind: 'not_required' });
      requestedRef.current = false;
      return;
    }
    if (subject.length === 0) {
      return;
    }
    const snap = loadOnboardingPaSnapshot(subject);
    if (snap !== null && snap.completed) {
      setPhase({ kind: 'completed' });
      return;
    }
    if (requestedRef.current) {
      return;
    }
    requestedRef.current = true;
    setPhase({ kind: 'loading' });
    const cancellation = { cancelled: false };
    void (async (): Promise<void> => {
      try {
        const { session } = await createOnboardingPaSession();
        if (cancellation.cancelled) return;
        saveOnboardingPaSnapshot(snapshotFromSessionDto(subject, session));
        const completed =
          typeof session.completed_at === 'string' && session.completed_at.length > 0;
        setPhase(completed ? { kind: 'completed' } : { kind: 'incomplete' });
      } catch (err) {
        if (cancellation.cancelled) return;
        const message =
          err instanceof OnboardingApiError
            ? (err.detail ?? err.message)
            : err instanceof Error
              ? err.message
              : 'unknown_error';
        setPhase({ kind: 'error', message });
      }
    })();
    return (): void => {
      cancellation.cancelled = true;
    };
  }, [isAuthenticated, isFunzionario, subject]);

  return phase;
}
